/**
 * Service Nutrition - Appels API pour les calculs nutritionnels
 *
 * Référence: docs/API_ENDPOINTS.md - Section "Nutrition (Coach)"
 */

import api from './api';

export const nutritionService = {
  /**
   * Calcule les macros d'un client pour un type de jour
   * @param {string} clientId - ID du client
   * @param {string} dayType - 'OFF' | 'ON' | 'INTENSIF'
   * @param {number} mealsCount - Nombre de repas (default: 5)
   */
  calculateMacros: (clientId, dayType, mealsCount = 5) =>
    api.post('/coach/nutrition/calculate-macros', { clientId, dayType, mealsCount }),

  /**
   * Calcule les macros pour tous les types de jours (OFF, ON, INTENSIF)
   * @param {string} clientId - ID du client
   * @param {number} mealsCount - Nombre de repas (default: 5)
   */
  calculateAllMacros: (clientId, mealsCount = 5) =>
    api.post('/coach/nutrition/calculate-all-macros', { clientId, mealsCount }),

  /**
   * Calcule le BMR et DEJ d'un client
   * @param {string} clientId - ID du client
   */
  calculateBmrDej: (clientId) =>
    api.post('/coach/nutrition/bmr-dej', { clientId }),

  /**
   * Recherche dans la base CIQUAL
   * @param {string} query - Terme de recherche (min 2 caractères)
   * @param {number} limit - Nombre max de résultats (default: 20)
   */
  searchCiqual: (query, limit = 20) =>
    api.get(`/coach/nutrition/ciqual/search?q=${encodeURIComponent(query)}&limit=${limit}`),

  /**
   * Récupère un aliment CIQUAL par son code
   * @param {string|number} code - Code CIQUAL
   */
  getCiqualByCode: (code) =>
    api.get(`/coach/nutrition/ciqual/${code}`),

  /**
   * Calcule les macros d'un ingrédient
   * @param {number} ciqual_code - Code CIQUAL de l'ingrédient
   * @param {number} quantity - Quantité en grammes
   */
  getIngredientMacros: (ciqual_code, quantity) =>
    api.post('/coach/nutrition/ingredient-macros', { ciqual_code, quantity }),

  /**
   * Calcule les macros d'une recette complète
   * @param {Array} ingredients - Liste des ingrédients [{ciqual_code, quantity, name, unit}]
   */
  getRecipeMacros: (ingredients) =>
    api.post('/coach/nutrition/recipe-macros', { ingredients }),

  /**
   * Ajuste une recette pour correspondre aux besoins d'un client
   * @param {Object} recipe - Recette avec ingredients
   * @param {string} clientId - ID du client
   * @param {string} dayType - 'OFF' | 'ON' | 'INTENSIF'
   * @param {number} mealsCount - Nombre de repas par jour
   */
  adjustRecipe: (recipe, clientId, dayType, mealsCount = 5) =>
    api.post('/coach/nutrition/adjust-recipe', { recipe, clientId, dayType, mealsCount }),

  /**
   * Ajuste toutes les recettes d'une journée
   * @param {Array} recipes - Liste des recettes de la journée
   * @param {string} clientId - ID du client
   * @param {string} dayType - 'OFF' | 'ON' | 'INTENSIF'
   */
  adjustDayRecipes: (recipes, clientId, dayType) =>
    api.post('/coach/nutrition/adjust-day-recipes', { recipes, clientId, dayType }),

  /**
   * Génère une liste de courses depuis un WeekPlan
   * @param {string} weekPlanId - ID du plan hebdomadaire
   */
  getShoppingListFromPlan: (weekPlanId) =>
    api.get(`/coach/nutrition/shopping-list/${weekPlanId}`),

  /**
   * Génère une liste de courses depuis des recettes
   * @param {Array} recipes - Liste des recettes
   */
  generateShoppingList: (recipes) =>
    api.post('/coach/nutrition/shopping-list', { recipes }),
};

export default nutritionService;
